/**
 * Flight preview warnings (newsletter MVP).
 *
 * Pure, side-effect-free inspection of a SendGrid template + its field mappings
 * that flags slots likely to render wrong — used by TemplatePreviewModal next to
 * the live render. Does NOT substitute or touch send-time resolution.
 *
 * Two checks:
 *  - escaped_html: a slot whose value is raw HTML is referenced as {{field}}
 *    (which SendGrid HTML-escapes → renders as visible text). Needs {{{field}}}.
 *  - bare_url: a content/url slot (a URL) is referenced as a bare {{field}} that
 *    is NOT inside an src=/href= attribute → renders as text, not an image/link.
 */
import type { FieldMapping } from "../services/templates.service";

import {
  CRM_SYSTEM_LINK_FIELDS,
  CRM_SYSTEM_LINK_NO_CATEGORY_WARNING,
  CRM_SYSTEM_LINK_NOTE,
  classifyFlightPreheader,
  classifyFlightSubject,
  FLIGHT_SUBJECT_CONDITIONAL_NOTE,
  MERGE_TOKEN_RE,
  flightFilledNote,
  type FlightFieldFill,
  isCrmSystemLinkField,
  isFlightPreheaderField,
  isFlightSubjectField,
} from "./systemTemplateVariables";

export type PreviewWarningKind = "escaped_html" | "bare_url";

export interface PreviewWarning {
  templateField: string;
  kind: PreviewWarningKind;
  message: string;
}

export interface DetectOptions {
  /** content item id -> resolved URL, for content-sourced slots */
  contentUrls?: Record<string, string>;
  /**
   * Does the flight have an email category (SendGrid unsubscribe group)? The CRM fills in
   * {{{crm_unsubscribe_url}}} / {{{crm_preferences_url}}} at send time only when it does.
   * undefined = not known here (treated like "yes": a neutral note, never a warning).
   */
  hasEmailCategory?: boolean;
  /**
   * The flight's subject line (campaign_flights.email_subject). The send path fills the exact
   * `subject` slot from it, overriding any field-mapper value, whenever it is non-empty.
   * undefined = not known here (no subject rule applied: `subject` is checked like any field).
   */
  flightSubject?: string | null;
  /**
   * The flight's preheader setting (campaign_flights.metadata.preheader). The send path passes it as
   * an extra field applied AFTER the field mappings, so it fills the exact `preheader` slot and wins
   * over any field-mapper value. undefined = not known here (no rule applied).
   */
  flightPreheader?: string | null;
  /**
   * SEND-TIME template data for resolving merge fields in the flight's subject line — exactly what the
   * send path's renderer will hold (see utils/flightSendTimeData.ts), never preview-only extras.
   * Omitted = nothing known: every merge field in the subject is treated as unknown.
   */
  mergeData?: Record<string, unknown>;
  /** Fields whose send-time value depends on data the preview doesn't have. */
  mergeUnknown?: ReadonlySet<string>;
}

const HTML_RE = /<[a-z!/][^>]*>/i;

// Block-level tags that are invalid inside a <p> (the browser auto-closes the
// <p>, which scrambles layout). Inline tags (<b>, <a>, <img>, <span>…) are fine.
const BLOCK_TAG_RE =
  /<\s*(?:div|table|thead|tbody|tfoot|tr|td|th|ul|ol|li|p|h[1-6]|section|article|header|footer|nav|aside|blockquote|figure|figcaption|hr|pre|form)\b/i;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function detectPreviewWarnings(
  templateHtml: string,
  mappings: FieldMapping[],
  opts: DetectOptions = {},
): PreviewWarning[] {
  const warnings: PreviewWarning[] = [];
  const html = templateHtml ?? "";

  for (const m of mappings) {
    if (!m.templateField || m.sourceType === "sendgrid") {
      continue;
    }
    const field = m.templateField;
    const esc = escapeRegExp(field);

    const usesDouble = new RegExp(
      `(?<!\\{)\\{\\{\\s*${esc}\\s*\\}\\}(?!\\})`,
    ).test(html);
    const usesTriple = new RegExp(`\\{\\{\\{\\s*${esc}\\s*\\}\\}\\}`).test(
      html,
    );
    // Not referenced at all → nothing to check.
    if (!usesDouble && !usesTriple) {
      continue;
    }

    // Value we can know at authoring time.
    let value = "";
    if (m.sourceType === "custom" || m.sourceType === "url") {
      value = m.customValue ?? "";
    } else if (m.sourceType === "content" && m.sourceField) {
      value = opts.contentUrls?.[m.sourceField] ?? "";
    }

    // escaped_html: only the escaping {{field}} form is risky (triple braces are
    // the FIX). Fires whenever {{field}} appears, even if {{{field}}} also appears.
    if (usesDouble && HTML_RE.test(value)) {
      warnings.push({
        templateField: field,
        kind: "escaped_html",
        message: `"${field}" contains HTML but the template uses {{${field}}}, which escapes it (it renders as visible text). Use {{{${field}}}} for raw HTML.`,
      });
      continue;
    }

    // bare_url: a content/url value referenced bare — DOUBLE OR TRIPLE braces —
    // outside a src=/href= attribute renders the URL as visible text, not an
    // image/link. (Triple-brace URL slots were previously skipped.)
    if (m.sourceType === "content" || m.sourceType === "url") {
      const inAttr = new RegExp(
        `(?:src|href)\\s*=\\s*["']?\\{\\{\\{?\\s*${esc}`,
        "i",
      ).test(html);
      if (!inAttr) {
        warnings.push({
          templateField: field,
          kind: "bare_url",
          message: `"${field}" is a URL referenced outside an src=/href= attribute, so it renders as visible text instead of an image or link. Wrap it, e.g. <img src="{{${field}}}">.`,
        });
      }
    }
  }

  return warnings;
}

// ---------------------------------------------------------------------------
// Template readiness — consolidates the precise checks above into ONE operator-
// facing verdict (Ready / Needs attention) with plain-language fixes. Warnings
// are reliable signals derived from data we have now (template HTML + mappings);
// notes are neutral/speculative (e.g. lookup values we can't see at authoring
// time) and never downgrade readiness.
// ---------------------------------------------------------------------------

export type ReadinessSeverity = "warning" | "note";

export interface ReadinessItem {
  field: string;
  severity: ReadinessSeverity;
  /** Plain-language description of the problem (no SendGrid jargon). */
  title: string;
  /** Plain-language exact fix. */
  fix: string;
}

export interface TemplateReadiness {
  status: "ready" | "needs_attention";
  items: ReadinessItem[];
}

// Mirror of isMappingEffectivelyEmpty in templates.service.ts, inlined so this
// module stays free of the supabase-coupled templates.service runtime (importing
// it as a value would make flightPreview throw without VITE env). Keep in sync.
function mappingValueMissing(m: FieldMapping): boolean {
  const blank = (s?: string): boolean => !s || !s.trim();
  switch (m.sourceType) {
    case "custom":
    case "url":
      return blank(m.customValue);
    case "contact":
    case "voucher":
    case "content":
      return blank(m.sourceField);
    case "lookup":
      return (
        blank(m.lookupId) ||
        blank(m.lookupValueColumn) ||
        (m.lookupResolution !== "default_row" && blank(m.lookupKeyContactField))
      );
    default:
      return false;
  }
}

// True ONLY when all three hold: the field's slot sits inside a <p>, the value is
// known now (custom source), and that value contains block-level HTML. Uses
// DOMParser (present in the browser + jsdom); degrades to false where DOMParser is
// unavailable (e.g. a plain-node test env) so this never throws.
function blockHtmlInsideParagraph(
  html: string,
  field: string,
  value: string,
): boolean {
  if (typeof DOMParser === "undefined") {
    return false;
  }
  if (!BLOCK_TAG_RE.test(value)) {
    return false;
  }
  const slot = new RegExp(`\\{\\{\\{?\\s*${escapeRegExp(field)}\\s*\\}\\}\\}?`);
  const doc = new DOMParser().parseFromString(html, "text/html");
  return Array.from(doc.querySelectorAll("p")).some((p) =>
    slot.test(p.innerHTML),
  );
}

export function computeTemplateReadiness(
  templateHtml: string,
  mappings: FieldMapping[],
  opts: DetectOptions = {},
): TemplateReadiness {
  const items: ReadinessItem[] = [];
  const html = templateHtml ?? "";

  // 2026-10-07 (round 2): decide exactly as the send path does. subject: substituted once, overrides only
  // when the result has a non-space character (empty or spaces -> the mapping governs). preheader: raw
  // value, same rule. See systemTemplateVariables.ts (flightOverrideValue).
  const subjectFill: FlightFieldFill | undefined =
    opts.flightSubject === undefined
      ? undefined
      : classifyFlightSubject(
          opts.flightSubject,
          opts.mergeData ?? {},
          opts.mergeData === undefined
            ? new Set(
                Array.from(
                  (opts.flightSubject ?? "").matchAll(MERGE_TOKEN_RE),
                  (mm) => mm[1],
                ),
              )
            : (opts.mergeUnknown ?? new Set()),
        );
  const preheaderFill: FlightFieldFill | undefined =
    opts.flightPreheader === undefined
      ? undefined
      : classifyFlightPreheader(opts.flightPreheader);
  const filledAtSend = (field: string): boolean =>
    isCrmSystemLinkField(field) ||
    (subjectFill === "filled" && isFlightSubjectField(field)) ||
    (preheaderFill === "filled" && isFlightPreheaderField(field));
  // Fields that get a flight-specific item below instead of the generic checks.
  const flightSpecial = (field: string): boolean =>
    isFlightSubjectField(field) && subjectFill === "conditional";
  const handled = (field: string): boolean =>
    filledAtSend(field) || flightSpecial(field);

  // PSL-1/PSL-2: a differently-cased system-link name ({{{CRM_Unsubscribe_URL}}}) is NOT injected —
  // the send path writes only the exact lowercase keys and variable lookup is case-sensitive.
  // Each such name gets exactly ONE casing-specific warning (below), worded by its mapping state,
  // and is excluded from the generic checks so they can't contradict it.
  const slotRe = /\{\{\{?\s*([A-Za-z0-9_]+)\s*\}\}\}?/g;
  const miscased = new Set<string>();
  for (const name of [
    ...Array.from(html.matchAll(slotRe), (mm) => mm[1]),
    ...mappings.map((m) => m.templateField ?? ""),
  ]) {
    if (
      name &&
      !isCrmSystemLinkField(name) &&
      CRM_SYSTEM_LINK_FIELDS.includes(name.toLowerCase())
    ) {
      miscased.add(name);
    }
  }

  // Present-but-empty mappings take priority: a blank value supersedes any
  // brace/URL advice for that field (there is nothing to render either way).
  const emptyFields = new Set<string>();
  for (const m of mappings) {
    if (
      m.templateField &&
      m.sourceType !== "sendgrid" &&
      !handled(m.templateField) &&
      !miscased.has(m.templateField) &&
      mappingValueMissing(m)
    ) {
      emptyFields.add(m.templateField);
    }
  }
  for (const field of emptyFields) {
    items.push({
      field,
      severity: "warning",
      title: `"${field}" has a source selected but nothing filled in, so it will appear blank in the email.`,
      fix: `Fill in its value in the field mapper, or remove the mapping if the slot isn't needed.`,
    });
  }

  // Precise brace/URL warnings (reliable — value is known now). Skip fields
  // already flagged empty so each field shows at most one warning.
  for (const w of detectPreviewWarnings(html, mappings, opts)) {
    if (
      emptyFields.has(w.templateField) ||
      handled(w.templateField) ||
      miscased.has(w.templateField)
    ) {
      continue;
    }
    if (w.kind === "escaped_html") {
      items.push({
        field: w.templateField,
        severity: "warning",
        title: `"${w.templateField}" contains formatting/HTML, but the template will show it as plain text — readers would see the tags.`,
        fix: `In the SendGrid template, change {{${w.templateField}}} to triple braces {{{${w.templateField}}}}.`,
      });
    } else {
      items.push({
        field: w.templateField,
        severity: "warning",
        title: `"${w.templateField}" is a link or image address, but the template prints it as text instead of showing the image/link.`,
        fix: `Wrap it in the template — e.g. <img src="{{${w.templateField}}}"> for an image, or <a href="{{${w.templateField}}}">…</a> for a link.`,
      });
    }
  }

  // Block-level HTML placed inside a <p> (custom source only — the value is
  // known now). Skip fields that already have a warning so each shows at most one.
  const warnedFields = new Set(
    items.filter((i) => i.severity === "warning").map((i) => i.field),
  );
  for (const m of mappings) {
    if (
      m.sourceType !== "custom" ||
      !m.templateField ||
      handled(m.templateField) ||
      miscased.has(m.templateField) ||
      warnedFields.has(m.templateField)
    ) {
      continue;
    }
    if (blockHtmlInsideParagraph(html, m.templateField, m.customValue ?? "")) {
      items.push({
        field: m.templateField,
        severity: "warning",
        title: `"${m.templateField}" holds a block of HTML (a paragraph, list, or table) but its slot sits inside a <p> in the template — that is invalid HTML and email clients lay it out unpredictably.`,
        fix: `In the SendGrid template, move {{{${m.templateField}}}} out of the <p>…</p> and into a <div>.`,
      });
    }
  }

  // Neutral notes — a lookup value isn't known here (it resolves per recipient);
  // if any of those values are HTML the slot needs triple braces. Never a
  // warning, never downgrades readiness.
  for (const m of mappings) {
    if (
      m.sourceType !== "lookup" ||
      !m.templateField ||
      emptyFields.has(m.templateField) ||
      handled(m.templateField) ||
      miscased.has(m.templateField)
    ) {
      continue;
    }
    const esc = escapeRegExp(m.templateField);
    const usesDouble = new RegExp(
      `(?<!\\{)\\{\\{\\s*${esc}\\s*\\}\\}(?!\\})`,
    ).test(html);
    if (usesDouble) {
      items.push({
        field: m.templateField,
        severity: "note",
        title: `"${m.templateField}" fills in per recipient from a lookup table, so its content can't be checked here.`,
        fix: `If those values contain HTML, set the template slot to triple braces {{{${m.templateField}}}}.`,
      });
    }
  }

  // The CRM's own unsubscribe / email-preference links (2026-10-06). Filled in at send time,
  // overriding any field-mapper entry — so never "empty" — but only when the flight has an
  // email category; without one nothing is injected and the links WOULD be blank.
  const usesSystemLink =
    mappings.some((m) => isCrmSystemLinkField(m.templateField)) ||
    CRM_SYSTEM_LINK_FIELDS.some((f) =>
      new RegExp(`\\{\\{\\{?\\s*${escapeRegExp(f)}\\s*\\}\\}\\}?`).test(html),
    );

  for (const name of miscased) {
    const lower = name.toLowerCase();
    const mapping = mappings.find(
      (m) => m.templateField === name && m.sourceType !== "sendgrid",
    );
    if (!mapping) {
      items.push({
        field: name,
        severity: "warning",
        title: `"${name}" will be empty: it isn't filled in automatically — only the exact lowercase name ${lower} is.`,
        fix: `In the SendGrid template, rename it to {{{${lower}}}}.`,
      });
    } else if (mappingValueMissing(mapping)) {
      items.push({
        field: name,
        severity: "warning",
        title: `"${name}" will be blank: it isn't filled in automatically (only the exact lowercase name ${lower} is), and its field-mapper entry is empty.`,
        fix: `In the SendGrid template, rename it to {{{${lower}}}}, then remove the "${name}" mapping.`,
      });
    } else {
      items.push({
        field: name,
        severity: "warning",
        title: `"${name}" is not the CRM's automatic link: only the exact lowercase name ${lower} is filled in automatically, so this slot shows the field-mapper value instead.`,
        fix: `In the SendGrid template, rename it to {{{${lower}}}} so the CRM fills in its signed link, then remove the "${name}" mapping.`,
      });
    }
  }
  if (usesSystemLink) {
    items.push(
      opts.hasEmailCategory === false
        ? {
            field: "crm_unsubscribe_url",
            severity: "warning",
            title: CRM_SYSTEM_LINK_NO_CATEGORY_WARNING,
            fix: "Choose an email category on the flight.",
          }
        : {
            field: "crm_unsubscribe_url",
            severity: "note",
            title: CRM_SYSTEM_LINK_NOTE,
            fix: "Nothing to do. A field-mapper entry for crm_unsubscribe_url or crm_preferences_url is ignored.",
          },
    );
  }

  // 2026-10-07: subject (flight subject line) and preheader (flight settings) are filled at send
  // time, overriding any field-mapper value. One combined note when both apply; the fix text says
  // which value wins when a field-mapper entry also supplies one.
  const usesSlot = (field: string, isField: (f: string) => boolean): boolean =>
    mappings.some((m) => isField(m.templateField ?? "")) ||
    new RegExp(`\\{\\{\\{?\\s*${field}\\s*\\}\\}\\}?`).test(html);
  const usesSubject = usesSlot("subject", isFlightSubjectField);
  const usesPreheader = usesSlot("preheader", isFlightPreheaderField);
  const subjectNote = subjectFill === "filled" && usesSubject;
  const preheaderNote = preheaderFill === "filled" && usesPreheader;
  const flightNote = flightFilledNote(subjectNote, preheaderNote);
  if (flightNote) {
    const overridden = mappings
      .filter(
        (m) =>
          m.sourceType !== "sendgrid" &&
          !mappingValueMissing(m) &&
          ((subjectNote && isFlightSubjectField(m.templateField)) ||
            (preheaderNote && isFlightPreheaderField(m.templateField))),
      )
      .map((m) => m.templateField);
    items.push({
      field: subjectNote ? "subject" : "preheader",
      severity: "note",
      title: flightNote,
      fix:
        overridden.length > 0
          ? `The flight's own setting is used; the field-mapper value for ${overridden.join(" and ")} is ignored.`
          : "Nothing to do. A field-mapper entry for these is ignored while the flight sets them.",
    });
  }

  const mapped = (isField: (f: string) => boolean): boolean =>
    mappings.some(
      (m) =>
        isField(m.templateField ?? "") &&
        m.sourceType !== "sendgrid" &&
        !mappingValueMissing(m),
    );
  if (subjectFill === "conditional" && usesSubject) {
    const hasMapped = mapped(isFlightSubjectField);
    items.push({
      field: "subject",
      severity: hasMapped ? "note" : "warning",
      title: FLIGHT_SUBJECT_CONDITIONAL_NOTE,
      fix: hasMapped
        ? "For a recipient whose merge fields are empty, your field-mapper value for subject is used instead. Pick a contact above to check one recipient."
        : "For a recipient whose merge fields are empty, the subject will be blank. Add fixed text to the flight's subject line, or a field-mapper fallback for subject. Pick a contact above to check one recipient.",
    });
  }
  const hasWarning = items.some((i) => i.severity === "warning");
  return { status: hasWarning ? "needs_attention" : "ready", items };
}
